import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import { LogoPontoAgil, NavbarContainer } from '@/global/components/Header'
import { TextField } from '@/global/components/TextField'
import Grid from '@/global/components/ui/Grid'
import Dialog from '@/global/guides/Dialog'
import { cn } from '@/lib/utils'
import { cpfValido } from '@acme/common'
import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import { ServerSideClock } from 'global/components/Clock'
import { FormField, FormFieldSelect } from 'global/components/Form'
import { Link } from 'global/components/Link'
import { ToggleTheme } from 'global/components/ToggleTheme'
import { LogInIcon } from 'lucide-react'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
    AvaliacaoSchema,
    EmpresasFuncionario,
    ErrorHandler,
    Registro,
    funcionario,
} from 'service/api'
import { toast } from 'sonner'
import { z } from 'zod'

const schema = z.object({
    cpf: z
        .string()
        .refine(cpfValido, {
            message: 'CPF inválido',
        })
        .transform(value => value.replace(/\D/g, '')),
    empresaId: z.number(),
    senha: AvaliacaoSchema.shape.senha,
})

type BaterPontoForm = z.infer<typeof schema>

export default function BaterPonto() {
    const [empresaId, setEmpresaId] = useState<number>(0)
    const [open, setOpen] = useState(false)
    const [ponto, setPonto] = useState<Registro>()
    const [empresas, setEmpresas] = useState<EmpresasFuncionario[]>([])

    const methods = useForm<BaterPontoForm>({
        resolver: zodResolver(schema),
    })

    const handleClose = () => {
        setOpen(false)
        methods.setValue('senha', '')
    }

    const handleChangeCPF = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        let { value } = event.target

        if ((value = String(value).replace(/[^0-9]/g, '')).length === 11) {
            try {
                const empresas = await funcionario.get<EmpresasFuncionario[]>(
                    'documento/' + value + '/empresas',
                )

                methods.setValue('empresaId', empresas[0].id)
                setEmpresaId(empresas[0].id)
                setEmpresas(empresas)
            } catch (error) {
                methods.setValue('empresaId', 0)
                setEmpresaId(0)
                setEmpresas([])
                console.error(error)
            }
        }
    }

    const handleSubmit = methods.handleSubmit(
        async data => {
            try {
                const ponto = await funcionario.post<BaterPontoForm, Registro>(
                    'bater-ponto',
                    data,
                )
                handleClose()
                setPonto(ponto)
            } catch (error) {
                handleClose()
                methods.setValue('senha', '')
                toast.error((error as ErrorHandler)?.message)
            }
        },
        error => toast.error(JSON.stringify(error)),
    )

    return [
        <NavbarContainer key="navbar">
            <LogoPontoAgil />
            <div className="min-w-0 flex-1" />
            <ToggleTheme />
            <IconButton
                title="Fazer login"
                className="dark:text-foreground text-accent"
                href="/"
            >
                <LogInIcon />
            </IconButton>
        </NavbarContainer>,
        <FormProvider key="form" {...methods}>
            <form
                className="h-screen flex justify-center"
                onSubmit={handleSubmit}
            >
                <div className="h-screen flex justify-center items-center">
                    <div className="flex flex-col gap-6 justify-center items-center">
                        <div
                            className={cn(
                                'bg-card flex flex-col justify-center items-center',
                                'rounded-2xl p-8 sm:w-96 w-full shadow-lg -mt-16',
                            )}
                        >
                            <div className="w-full gap-2 flex flex-col justify-center items-center">
                                <ServerSideClock className="pb-6" />
                                <FormField
                                    name="cpf"
                                    label="CPF"
                                    placeholder="000.000.000-00"
                                    required
                                    onChange={handleChangeCPF}
                                    options={{
                                        blocks: [3, 3, 3, 2],
                                        delimiters: ['.', '.', '-'],
                                    }}
                                />
                                {empresaId !== 0 ? (
                                    <FormFieldSelect
                                        name="empresaId"
                                        label="Empresa"
                                        disabled={empresas.length < 2}
                                        required
                                        options={empresas.map(empresa => ({
                                            value: empresa.id,
                                            label: empresa.nomeFantasia,
                                        }))}
                                    />
                                ) : (
                                    <FormField
                                        name="empresaId"
                                        label="Empresa"
                                        disabled
                                    />
                                )}
                                <Button
                                    fullWidth
                                    size="large"
                                    className="mt-2"
                                    onClick={() => setOpen(true)}
                                >
                                    Bater ponto
                                </Button>
                            </div>
                            <span className="mt-4 text-center text-xs">
                                Registre o ponto com CPF ou faça{' '}
                                <Link href="/">login.</Link>.
                            </span>
                        </div>
                    </div>
                </div>
            </form>
            <DialogBaterPonto
                open={open}
                setOpen={setOpen}
                methods={methods}
                handleSubmit={handleSubmit}
            />
            <DialogPontoRegistrado
                methods={methods}
                ponto={ponto}
                setPonto={setPonto}
            />
        </FormProvider>,
    ]
}

interface IProps {
    open: boolean
    setOpen: (open: boolean) => void
    methods: ReturnType<typeof useForm<BaterPontoForm>>
    handleSubmit: (data: any) => void
}

function DialogBaterPonto({ open, setOpen, methods, handleSubmit }: IProps) {
    return (
        <Dialog.Root
            open={open}
            onClose={() => {
                setOpen(false)
                methods.setValue('senha', '')
            }}
            width="lg"
        >
            <Dialog.Title>
                Digite sua senha para registrar o ponto.
            </Dialog.Title>
            <Dialog.Content>
                <TextField
                    {...methods.register('senha')}
                    className="mt-2"
                    label="Senha"
                    type="password"
                    autoComplete="current-password"
                    unHelperText
                    error={methods.formState.errors.senha?.message as string}
                />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Button onClick={() => handleSubmit(methods.getValues())}>
                    Confirmar
                </Button>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

interface IPropsPontoRegistrado {
    ponto: Registro | undefined
    setPonto: (ponto: Registro | undefined) => void
    methods: IProps['methods']
}

function DialogPontoRegistrado({
    ponto,
    setPonto,
    methods,
}: IPropsPontoRegistrado) {
    const resetPonto = () => {
        setPonto(undefined)
        methods.setValue('senha', '')
    }

    return (
        <Dialog.Root open={!!ponto} onClose={resetPonto} width="lg">
            <Dialog.Title>Ponto registrado com sucesso!</Dialog.Title>
            <Dialog.Content className="overflow-y-hidden">
                <Grid container>
                    <Grid item xs={6}>
                        <TextField
                            label="Data"
                            unHelperText
                            disabled
                            value={dayjs(ponto?.data).format('DD/MM/YYYY')}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Hora"
                            unHelperText
                            disabled
                            value={ponto?.hora}
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton>
                    Fechar
                </Dialog.Actions.SecondaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}
